import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { organizationSchema, websiteSchema, localBusinessSchema, breadcrumbSchema, faqSchema } from '../utils/structuredData';

const ExpeditionsPage = () => {
  // Enhanced performance tracking and accessibility setup
  useEffect(() => {
    // Report Core Web Vitals for performance monitoring
    if ('web-vital' in window) {
      window.webVitals?.getCLS(console.log);
      window.webVitals?.getFID(console.log);
      window.webVitals?.getFCP(console.log);
      window.webVitals?.getLCP(console.log);
      window.webVitals?.getTTFB(console.log);
    }

    // Enhanced keyboard navigation for accessibility
    const handleKeyDown = (e) => {
      // Skip to main content on keyboard navigation
      if (e.ctrlKey || e.metaKey) return;
      
      // Escape key to close any open modals or menus
      if (e.code === 'Escape') {
        const focusedElement = document.activeElement;
        focusedElement?.blur();
      }
      
      // Improve focus management for interactive elements
      if (e.code === 'Space' || e.code === 'Enter') {
        const target = e.target;
        if (target.getAttribute('role') === 'button' && !target.href) {
          e.preventDefault();
          target.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Enhanced structured data for comprehensive SEO optimization
  const expeditionsSchema = [
    {
      ...organizationSchema,
      // Ensure correct domain usage
      url: "https://emberimpact.com",
      logo: "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
      description: "EMBER Impact Ventures transforms leaders through authentic community, research-backed practices, and innovative programs including The Crucible peer accelerator, Spark Moments™, and LIT Expeditions.",
      email: "hello@emberimpact.com"
    },
    {
      ...websiteSchema,
      url: "https://emberimpact.com",
      potentialAction: {
        "@type": "SearchAction",
        "target": "https://emberimpact.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "LIT Expeditions™ - Learning, Innovation, and Transformation",
      "description": "Transformational 4-7 day leadership experiences that push you beyond your comfort zone through adventure-based learning. Expert guides, small groups (max 15), challenge-based learning, and integration support. Launching Spring/Summer 2026.",
      "provider": {
        "@type": "Organization",
        "name": "EMBER Impact Ventures LLC",
        "url": "https://emberimpact.com",
        "logo": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
        "email": "hello@emberimpact.com"
      },
      "serviceType": "Leadership Development Expedition",
      "category": "Adventure-Based Leadership Training",
      "areaServed": "Worldwide",
      "availableChannel": {
        "@type": "ServiceChannel",
        "availableLanguage": "English",
        "serviceUrl": "https://emberimpact.com/expeditions"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/PreOrder",
        "availabilityStarting": "2026-03-01",
        "description": "4-7 day transformational leadership expeditions launching Spring/Summer 2026. Small groups of max 15 leaders with expert guides.",
        "seller": {
          "@type": "Organization",
          "name": "EMBER Impact Ventures LLC",
          "url": "https://emberimpact.com"
        }
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Leadership Expedition Programs",
        "description": "Adventure-based transformational leadership experiences",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "LIT Expedition Experience",
              "description": "4-7 day intensive leadership transformation experience with expert guides, challenge-based learning, and integration support",
              "audience": {
                "@type": "BusinessAudience",
                "audienceType": "Senior Leaders, Executives, Entrepreneurs"
              }
            }
          }
        ]
      },
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Duration",
          "value": "4-7 days"
        },
        {
          "@type": "PropertyValue", 
          "name": "Group Size",
          "value": "Maximum 15 participants"
        },
        {
          "@type": "PropertyValue",
          "name": "Launch Date", 
          "value": "Spring/Summer 2026"
        },
        {
          "@type": "PropertyValue",
          "name": "Fitness Level",
          "value": "All fitness levels welcome"
        }
      ]
    },
    localBusinessSchema,
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "LIT Expeditions", url: "/expeditions" }
    ]),
    faqSchema([
      {
        question: "What are LIT Expeditions?",
        answer: "LIT Expeditions are 4-7 day transformational leadership experiences that combine adventure-based learning with cutting-edge leadership development. They push you beyond your comfort zone to discover breakthrough insights about your leadership potential."
      },
      {
        question: "Who are LIT Expeditions designed for?",
        answer: "LIT Expeditions are intensive experiences for serious leaders, executives, and entrepreneurs ready for transformational growth. Our application process ensures participants are prepared for the challenge."
      },
      {
        question: "What's the group size for expeditions?",
        answer: "We maintain small groups with a maximum of 15 leaders per expedition to ensure personalized attention and meaningful peer connections with expert guides."
      },
      {
        question: "When do LIT Expeditions launch?",
        answer: "LIT Expeditions are launching Spring/Summer 2026. Join our waitlist to be the first to know when applications open and receive early access."
      }
    ])
  ];

  return (
    <>
      <SEOHead 
        title="LIT Expeditions™ | Learning Innovation Transformation | EMBER"
        description="Immersive learning expeditions in real-world innovation environments. From agritech breakthroughs in the Netherlands to sustainable practices worldwide."
        keywords="LIT expeditions, leadership expeditions, transformational experiences, adventure leadership, leadership development, outdoor leadership, executive retreats, learning innovation transformation, breakthrough insights, leadership adventure, adventure-based learning, small group expeditions, leadership transformation, executive coaching, leadership challenges"
        ogImage="https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
        canonicalUrl="/expeditions"
        structuredData={expeditionsSchema}
      />

      {/* Preconnect for performance optimization - Mobile First */}
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Enhanced Skip Links for WCAG AAA Compliance */}
      <a 
        href="#main-content" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '6px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>
      <a 
        href="#waitlist-cta" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '200px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to waitlist
      </a>

      {/* Enhanced Mobile-First CSS and Performance Optimizations */}
      <style>{`
        /* Mobile-first CSS Variables and Reset */
        :root {
          --ember-fire: #FF5722;
          --ember-flame: #FF7043;
          --ember-heat: #FF6F00;  
          --ember-spark: #FFB74D;
          --ember-glow: #FFCC80;
          --ember-depth: #673AB7;
          --ember-cool: #455A64;
          --ember-flow: #9C27B0;
          --text-primary: #212121;
          --text-secondary: #455A64;
          --neutral-warm: #FFF8F0;
          --neutral-light: #F5F5F5;
          --neutral-medium: #757575;
          --neutral-dark: #424242;
          --neutral-charcoal: #263238;
        }
        
        /* Mobile-first reset */
        * {
          box-sizing: border-box;
        }
        
        /* Enhanced focus management - WCAG AAA */
        *:focus {
          outline: 3px solid #FFC107 !important;
          outline-offset: 2px !important;
          border-radius: 2px;
        }
        
        /* Mobile-first container styles */
        .expeditions-page-container { 
          contain: layout style paint;
          will-change: transform;
          min-height: 100vh;
        }
        
        /* Performance optimizations */
        .expedition-section {
          contain: layout style paint;
          transform: translateZ(0);
        }
        
        /* Mobile-first section spacing */
        section {
          contain: layout style paint;
          padding: 2rem 0; /* Mobile base padding */
        }
        
        /* Mobile-first typography */
        h1, h2, h3 { 
          line-height: 1.2;
          margin-bottom: 1rem;
        }
        
        h1 {
          font-size: 1.75rem; /* 28px mobile */
        }
        
        h2 {
          font-size: 1.5rem; /* 24px mobile */
        }
        
        h3 {
          font-size: 1.25rem; /* 20px mobile */
        }
        
        /* Mobile-first button styles with WCAG compliance */
        button, .btn, a[href], Link {
          min-height: 44px; /* WCAG minimum touch target */
          min-width: 44px;
          font-size: 16px; /* Minimum readable size */
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
        }
        
        /* Skip Navigation - WCAG AAA */
        .skip-nav {
          background: var(--ember-fire);
          color: white;
          text-decoration: none;
          border-radius: 4px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          z-index: 999999;
        }

        .skip-nav:focus {
          outline: 3px solid #FFC107;
          outline-offset: 3px;
        }
        
        /* Screen reader only content */
        .sr-only {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }
        
        /* Performance optimizations */
        img {
          content-visibility: auto;
          contain-intrinsic-size: 400px 300px;
          loading: lazy;
        }
        
        /* Mobile-first container optimization */
        .expedition-container {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        
        /* Tablet styles - 640px and up */
        @media (min-width: 640px) {
          section {
            padding: 3rem 0;
          }
          
          .expedition-container {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
          
          h1 {
            font-size: 2.25rem; /* 36px tablet */
          }
          
          h2 {
            font-size: 1.875rem; /* 30px tablet */
          }
          
          h3 {
            font-size: 1.5rem; /* 24px tablet */
          }
          
          button, .btn, a[href] {
            font-size: 18px;
          }
        }
        
        /* Desktop styles - 1024px and up */
        @media (min-width: 1024px) {
          section {
            padding: 4rem 0;
          }
          
          h1 {
            font-size: 3rem; /* 48px desktop */
          }
          
          h2 {
            font-size: 2.25rem; /* 36px desktop */
          }
          
          h3 {
            font-size: 1.875rem; /* 30px desktop */
          }
        }
        
        /* Large desktop - 1440px and up */
        @media (min-width: 1440px) {
          h1 {
            font-size: 4rem; /* 64px large desktop */
          }
        }
        
        /* High contrast mode support - WCAG AAA */
        @media (prefers-contrast: high) {
          :root {
            --text-primary: #000000;
            --text-secondary: #000000;
            --ember-fire: #CC0000;
          }
          
          button, input, a, .bg-white {
            border: 2px solid currentColor !important;
          }
        }
        
        /* Reduced motion support - WCAG AAA */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
        
        /* Print styles */
        @media print {
          .skip-nav,
          button,
          .btn {
            display: none !important;
          }
          
          body {
            background: white !important;
            color: black !important;
          }
          
          * {
            background: white !important;
            color: black !important;
          }
        }
        
        /* Focus visible for modern browsers */
        button:focus-visible,
        a:focus-visible {
          outline: 3px solid #FFC107;
          outline-offset: 3px;
        }
        
        /* Enhanced accessibility for keyboard users */
        .skip-nav:focus,
        button:focus,
        input:focus,
        a:focus {
          z-index: 999999;
        }
        
        /* Error boundary styling */
        .error-boundary {
          padding: 2rem;
          text-align: center;
          background: #FFF3E0;
          border: 1px solid #FFB74D;
          border-radius: 8px;
          margin: 1rem;
        }
        
        /* Mobile-specific improvements */
        @media (max-width: 640px) {
          .expedition-container {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
          
          h1, h2, h3 {
            line-height: 1.2 !important;
          }
          
          /* Improve touch targets on mobile */
          button, a[role="button"] {
            min-height: 48px;
            min-width: 48px;
          }
        }
      `}</style>
      <Navigation />
      
      <main 
        id="main-content" 
        role="main" 
        className="expeditions-page-container"
      >
        <div 
          className="expedition-section expedition-container"
          style={{
            fontFamily: "'Inter', sans-serif",
            background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 25%, #FFF8F0 50%, #FF5722 75%, #E91E63 100%)',
            minHeight: '100vh',
            paddingTop: '80px', /* Match HomePage pt-20 exactly */
            paddingLeft: 'clamp(16px, 4vw, 20px)',
            paddingRight: 'clamp(16px, 4vw, 20px)',
            paddingBottom: '20px',
            lineHeight: '1.6'
          }}
        >
        
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            background: 'linear-gradient(135deg, #FF9800, #FF5722)',
            color: 'white',
            padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 24px)',
            borderRadius: '25px',
            fontSize: 'clamp(0.8rem, 2vw, 1rem)',
            fontWeight: '700',
            display: 'inline-block',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
          }} role="banner" aria-label="Announcement: EMBER Expeditions launching Spring Summer 2026">
            <span aria-hidden="true">🏔️</span> LAUNCHING SPRING/SUMMER 2026
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: '24px',
            padding: 'clamp(24px, 5vw, 48px) clamp(20px, 4vw, 40px)',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            marginBottom: '32px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{
              width: 'clamp(60px, 12vw, 80px)',
              height: 'clamp(60px, 12vw, 80px)',
              background: 'linear-gradient(135deg, #FF5722, #E91E63)',
              borderRadius: '16px',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(24px, 5vw, 32px)',
              fontWeight: '700',
              color: 'white'
            }} aria-hidden="true">
              ⛰️
            </div>
            
            <h1 style={{
              fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #FF5722, #E91E63, #9C27B0)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textAlign: 'center',
              marginBottom: '20px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}>
              LIT Expeditions™
            </h1>
            <p style={{
              fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
              color: '#2C3E50',
              textAlign: 'center',
              marginBottom: '30px',
              fontWeight: '600',
              letterSpacing: '0.05em'
            }}>
              Learning • Innovation • Transformation
            </p>
            <p style={{
              fontSize: 'clamp(1rem, 3vw, 1.3rem)',
              color: '#424242',
              marginBottom: '32px',
              lineHeight: '1.7',
              maxWidth: '650px',
              marginLeft: 'auto',
              marginRight: 'auto',
              textAlign: 'center',
              fontWeight: '400'
            }}>
              <strong>4-7 day transformational leadership experiences</strong> that push you beyond your comfort zone through adventure-based learning. Discover who you become when everything familiar is stripped away. <strong>Max 15 leaders per expedition.</strong>
            </p>
            
            <p style={{
              fontStyle: 'italic',
              fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
              color: '#E91E63',
              marginBottom: '40px',
              fontWeight: '500'
            }}>
              "You cannot discover new oceans unless you have the courage to lose sight of the shore."
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 480 ? '1fr' : 'repeat(2, 1fr)',
              gap: '24px',
              marginBottom: '40px',
              textAlign: 'center',
              maxWidth: '400px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              <div style={{
                background: 'rgba(255, 193, 7, 0.1)',
                borderRadius: '16px',
                padding: 'clamp(16px, 4vw, 24px) clamp(12px, 3vw, 20px)',
                border: '1px solid rgba(255, 193, 7, 0.2)'
              }}>
                <span style={{
                  fontSize: 'clamp(2rem, 5vw, 2.5rem)',
                  fontWeight: '800',
                  color: '#E91E63',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  4-7
                </span>
                <div style={{
                  color: '#424242',
                  fontWeight: '600',
                  fontSize: 'clamp(0.8rem, 2vw, 0.9rem)'
                }}>
                  Days
                </div>
              </div>
              <div style={{
                background: 'rgba(255, 193, 7, 0.1)',
                borderRadius: '16px',
                padding: 'clamp(16px, 4vw, 24px) clamp(12px, 3vw, 20px)',
                border: '1px solid rgba(255, 193, 7, 0.2)'
              }}>
                <span style={{
                  fontSize: 'clamp(2rem, 5vw, 2.5rem)',
                  fontWeight: '800',
                  color: '#E91E63',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  15
                </span>
                <div style={{
                  color: '#424242',
                  fontWeight: '600',
                  fontSize: 'clamp(0.8rem, 2vw, 0.9rem)'
                }}>
                  Max Group Size
                </div>
              </div>
            </div>
          </div>
          
          {/* Why EMBER Expeditions Transform Leaders */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: '24px',
            padding: 'clamp(24px, 4vw, 40px)',
            marginBottom: '32px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: '800',
              color: '#424242',
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              Why EMBER Expeditions Transform Leaders
            </h2>
            <p style={{ textAlign: 'center', color: '#424242', marginBottom: '40px', fontSize: 'clamp(1rem, 2.5vw, 1.1rem)' }}>
              We combine proven adventure education principles with cutting-edge leadership development to create experiences that change you at your core.
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
              gap: '32px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 10vw, 64px)',
                  height: 'clamp(48px, 10vw, 64px)',
                  background: 'linear-gradient(135deg, #FF5722, #E91E63)',
                  borderRadius: '16px',
                  margin: '0 auto 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(20px, 4vw, 28px)',
                  color: 'white'
                }} aria-hidden="true">
                  🎯
                </div>
                <h3 style={{
                  fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                  fontWeight: '700',
                  color: '#424242',
                  marginBottom: '12px'
                }}>
                  Expert Guides
                </h3>
                <p style={{
                  color: '#424242',
                  lineHeight: '1.6',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}>
                  Learn from world-class facilitators who blend adventure expertise with deep leadership wisdom.
                </p>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 10vw, 64px)',
                  height: 'clamp(48px, 10vw, 64px)',
                  background: 'linear-gradient(135deg, #FF5722, #E91E63)',
                  borderRadius: '16px',
                  margin: '0 auto 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(20px, 4vw, 28px)',
                  color: 'white'
                }} aria-hidden="true">
                  👥
                </div>
                <h3 style={{
                  fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                  fontWeight: '700',
                  color: '#424242',
                  marginBottom: '12px'
                }}>
                  Small Groups
                </h3>
                <p style={{
                  color: '#424242',
                  lineHeight: '1.6',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}>
                  Small groups (no larger than 15) ensure personalized attention and meaningful peer connections.
                </p>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 10vw, 64px)',
                  height: 'clamp(48px, 10vw, 64px)',
                  background: 'linear-gradient(135deg, #FF5722, #E91E63)',
                  borderRadius: '16px',
                  margin: '0 auto 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(20px, 4vw, 28px)',
                  color: 'white'
                }} aria-hidden="true">
                  ⚡
                </div>
                <h3 style={{
                  fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                  fontWeight: '700',
                  color: '#424242',
                  marginBottom: '12px'
                }}>
                  Challenge-Based Learning
                </h3>
                <p style={{
                  color: '#424242',
                  lineHeight: '1.6',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}>
                  Real challenges create real growth - step outside your comfort zone and discover your potential.
                </p>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 10vw, 64px)',
                  height: 'clamp(48px, 10vw, 64px)',
                  background: 'linear-gradient(135deg, #FF5722, #E91E63)',
                  borderRadius: '16px',
                  margin: '0 auto 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(20px, 4vw, 28px)',
                  color: 'white'
                }} aria-hidden="true">
                  🎯
                </div>
                <h3 style={{
                  fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                  fontWeight: '700',
                  color: '#424242',
                  marginBottom: '12px'
                }}>
                  Integration Support
                </h3>
                <p style={{
                  color: '#424242',
                  lineHeight: '1.6',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}>
                  Post-expedition coaching helps you integrate insights into your daily leadership practice.
                </p>
              </div>
            </div>
          </div>
          
          {/* Process Preview */}
          <div 
            id="waitlist-cta"
            style={{
            background: 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)',
            borderRadius: '24px',
            color: 'white',
            padding: 'clamp(24px, 4vw, 40px)',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: '700',
              marginBottom: '16px'
            }}>
              Ready to Transform Your Leadership?
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '40px',
              fontSize: 'clamp(1rem, 2.5vw, 1.1rem)'
            }}>
              EMBER Expeditions are intensive experiences for serious leaders. Our application process ensures you're ready for the challenge.
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 480 ? '1fr' : window.innerWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: window.innerWidth < 480 ? '20px' : '24px',
              marginBottom: '40px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 8vw, 56px)',
                  height: 'clamp(48px, 8vw, 56px)',
                  borderRadius: '16px',
                  margin: '0 auto 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '800',
                  color: 'white',
                  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                  background: 'linear-gradient(135deg, #E91E63, #9C27B0)'
                }}>
                  01
                </div>
                <div style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                  fontWeight: '700',
                  marginBottom: '8px'
                }}>
                  Application
                </div>
                <div style={{
                  fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.4',
                  padding: '0 8px'
                }}>
                  Submit your leadership background and expedition goals
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 8vw, 56px)',
                  height: 'clamp(48px, 8vw, 56px)',
                  borderRadius: '16px',
                  margin: '0 auto 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '800',
                  color: 'white',
                  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                  background: 'linear-gradient(135deg, #FF5722, #E91E63)'
                }}>
                  02
                </div>
                <div style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                  fontWeight: '700',
                  marginBottom: '8px'
                }}>
                  Interview
                </div>
                <div style={{
                  fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.4',
                  padding: '0 8px'
                }}>
                  Deep conversation about your readiness and expectations
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 8vw, 56px)',
                  height: 'clamp(48px, 8vw, 56px)',
                  borderRadius: '16px',
                  margin: '0 auto 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '800',
                  color: 'white',
                  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                  background: 'linear-gradient(135deg, #FF9800, #FF5722)'
                }}>
                  03
                </div>
                <div style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                  fontWeight: '700',
                  marginBottom: '8px'
                }}>
                  Preparation
                </div>
                <div style={{
                  fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.4',
                  padding: '0 8px'
                }}>
                  Pre-expedition coaching and physical/mental preparation
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'clamp(48px, 8vw, 56px)',
                  height: 'clamp(48px, 8vw, 56px)',
                  borderRadius: '16px',
                  margin: '0 auto 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '800',
                  color: 'white',
                  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                  background: 'linear-gradient(135deg, #FFC107, #FF9800)'
                }}>
                  04
                </div>
                <div style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                  fontWeight: '700',
                  marginBottom: '8px'
                }}>
                  Expedition
                </div>
                <div style={{
                  fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.4',
                  padding: '0 8px'
                }}>
                  4-7 days of transformational leadership challenges
                </div>
              </div>
            </div>
            
            <Link 
              to="/waitlist"
              style={{
                background: 'linear-gradient(135deg, #FFC107, #FF5722, #E91E63)',
                color: 'white',
                padding: 'clamp(14px, 3vw, 18px) clamp(28px, 5vw, 36px)',
                border: 'none',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(255, 87, 34, 0.3)',
                cursor: 'pointer',
                fontFamily: "'Inter', sans-serif"
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 25px rgba(255, 87, 34, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(255, 87, 34, 0.3)';
              }}
            >
              Join the Waitlist
            </Link>
            
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', marginTop: '16px' }}>
              Investment varies by expedition • All fitness levels welcome
            </p>
          </div>
          
          <div style={{
            textAlign: 'center',
            color: '#424242',
            fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
            lineHeight: '1.6',
            marginTop: '32px'
          }}>
            <strong>EMBER Expeditions combine adventure and leadership development for transformational growth.</strong><br/>
            Join our waitlist to be the first to know when applications open for Spring/Summer 2026.<br/><br/>
            Questions? Contact us at hello@emberimpact.com
          </div>
        </div>
      </div>
      </main>
      <Footer />
    </>
  );
};

export default ExpeditionsPage;